import { createElement, useCallback, useMemo } from 'react';
import { filter, isEmpty, isNil, map } from 'lodash';
import { useStore } from 'common/store';
import { Rows } from 'common/types/scalarSpreadsheet';
import { UseStoreValues } from 'common/types/store';
import { COLUMN_TYPES } from 'common/types/summaryDefinitions';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import {
  FIRM_SUMMARY_SPREADSHEET_EXCLUDED_ROWS_COUNT,
  FIRM_SUMMARY_SPREADSHEET_NAME,
  FIRM_SUMMARY_SPREADSHEET_TABLE_TERMS,
  FIRM_SUMMARY_SPREADSHEET_TITLE_ALIAS,
} from 'pages/Firms/common/constants';
import { SortIndicator } from 'pages/Firms/components';
import { useCellCustomClasses } from 'pages/Funds/hooks';
import { getObjectValue } from 'utilities';
import { createColumns, createParser, createRows, FirmSummaryColumn, FirmSummaryParser } from './config';
import { getOrderedColumnTypes, getRenderedFirmSummaryColumnTypes } from './config/createColumns';
import {
  FirmSummarySpreadsheet,
  ParentFundByCompany,
  UseFirmSummarySheetParams,
  UseFirmSummarySheetResult,
} from './useFirmSummarySheet.types';

const useFirmSummarySheet = (params: UseFirmSummarySheetParams): UseFirmSummarySheetResult => {
  const {
    firmGrossIRR,
    fundsAvailable,
    fundsByCompany,
    fundsGrossIRR,
    isAllFundsSelected,
    isProcessManagementEnabled,
    measurementDateName,
    selectedFund,
    setSelectedFund,
    spreadsheetDefinition,
    tasksProgress,
    onSortToggle,
    onProcessManagementTaskClick,
  } = params;

  const [storeValue] = useStore() as UseStoreValues;
  const { firmInfo: currentFirm } = getObjectValue(storeValue);
  const { slug: currentFirmSlug } = getObjectValue(currentFirm);

  const { cellCustomClasses: firmSummaryClassNames } = useCellCustomClasses();

  const parentFundsByCompany = useMemo<ParentFundByCompany[]>(
    () =>
      filter(
        map(
          fundsByCompany,
          (fund, rowNumber) =>
            ({
              isDraft: !fund.is_published && !fund.is_final,
              // Parent or standalone | when filtering by fund, all rows are parent
              isParent: isAllFundsSelected ? fund.is_parent || (!fund.is_parent && isNil(fund.parent_key)) : true,
              rowNumber: rowNumber + FIRM_SUMMARY_SPREADSHEET_EXCLUDED_ROWS_COUNT + 1, // +1 for 0-based index
            }) as ParentFundByCompany
        ),
        fund => fund.isParent
      ),
    [fundsByCompany, isAllFundsSelected]
  );

  const parentFundsByCompanyRows = useMemo<number[]>(
    () => map(parentFundsByCompany, 'rowNumber'),
    [parentFundsByCompany]
  );

  const nonDraftParentFundsByCompanyRows = useMemo<number[]>(
    () =>
      map(
        filter(parentFundsByCompany, row => !row.isDraft),
        'rowNumber'
      ),
    [parentFundsByCompany]
  );

  const sortKeys = spreadsheetDefinition?.sortKeys;

  const rows = useMemo<Rows>(() => {
    const baseRows = createRows({ firmSlug: currentFirmSlug, fundsByCompany, isAllFundsSelected });

    if (onSortToggle) {
      const titleRow = baseRows.find(r => r.alias === FIRM_SUMMARY_SPREADSHEET_TITLE_ALIAS);
      if (titleRow) {
        const currentSortKey = sortKeys?.find(([col]) => col === COLUMN_TYPES.COMPANY_NAME);
        const sortDirection = currentSortKey ? currentSortKey[1] : null;
        titleRow.value = createElement(SortIndicator, {
          title: String(titleRow.value),
          direction: sortDirection,
          onClick: () => onSortToggle(COLUMN_TYPES.COMPANY_NAME),
        });
        titleRow.forceComponent = true;
      }
    }

    return baseRows;
  }, [currentFirmSlug, fundsByCompany, isAllFundsSelected, onSortToggle, sortKeys]);

  const orderedColumnTypes = useMemo(
    () => getOrderedColumnTypes(spreadsheetDefinition),
    [spreadsheetDefinition]
  );

  const renderedColumnTypes = useMemo(
    () => getRenderedFirmSummaryColumnTypes(orderedColumnTypes, isProcessManagementEnabled),
    [isProcessManagementEnabled, orderedColumnTypes]
  );

  const columns = useMemo<FirmSummaryColumn[]>(() => {
    if (!isEmpty(fundsByCompany)) {
      return createColumns({ fundsByCompany, spreadsheetDefinition, orderedColumnTypes: renderedColumnTypes });
    }

    return [];
  }, [fundsByCompany, renderedColumnTypes, spreadsheetDefinition]);

  const parser: FirmSummarySpreadsheet['parser'] = useCallback(
    (parserParams: FirmSummaryParser) =>
      createParser({
        ...parserParams,
        firmGrossIRR,
        firmSummaryClassNames,
        fundsAvailable,
        fundsGrossIRR,
        isProcessManagementEnabled,
        nonDraftParentFundsByCompanyRows,
        measurementDateName,
        parentFundsByCompanyRows,
        selectedFund,
        setSelectedFund,
        orderedColumnTypes: renderedColumnTypes,
        breakevenMultiple: spreadsheetDefinition?.breakevenMultiple,
        onSortToggle,
        sortKeys: spreadsheetDefinition?.sortKeys,
        isEnhancedMode: true,
        tasksProgress,
        onProcessManagementTaskClick,
      }),
    [
      firmGrossIRR,
      firmSummaryClassNames,
      fundsAvailable,
      fundsGrossIRR,
      isProcessManagementEnabled,
      measurementDateName,
      nonDraftParentFundsByCompanyRows,
      parentFundsByCompanyRows,
      selectedFund,
      setSelectedFund,
      renderedColumnTypes,
      spreadsheetDefinition?.breakevenMultiple,
      onSortToggle,
      onProcessManagementTaskClick,
      spreadsheetDefinition?.sortKeys,
      tasksProgress,
    ]
  );

  // Spreadsheet config
  const spreadsheet = useMemo<FirmSummarySpreadsheet | null>(() => {
    if (!isEmpty(columns)) {
      // @ts-expect-error: SpreadsheetConfigConstructorParams requires runtime methods not supplied to constructor configs.
      const spreadsheetConfig: FirmSummarySpreadsheet = new SpreadsheetConfig({
        allowConfirmAndDeleteColumn: false,
        allowDeleteRow: true,
        alwaysDisplayLegend: false,
        colTitleRow: 0,
        columns,
        currencyFormatter: true,
        format: undefined,
        hasColTitle: true,
        name: FIRM_SUMMARY_SPREADSHEET_NAME,
        page: FIRM_SUMMARY_SPREADSHEET_NAME,
        parser,
        reverseParser: undefined,
        rowConfig: rows,
        showPreviousColsDivider: false,
        showTitlesColumn: true,
        showToolbar: true,
        showTotalColumn: false,
        tableData: { columns },
        tableTerms: FIRM_SUMMARY_SPREADSHEET_TABLE_TERMS,
        totalParser: undefined,
        unitsFormatter: true,
      });

      return spreadsheetConfig;
    }

    return null;
  }, [columns, parser, rows]);

  return {
    spreadsheet,
  };
};

export default useFirmSummarySheet;
